import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { HelpPanel } from '../components/ui/help-panel';
import { billsHelp } from '../lib/helpContent';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Receipt,
  Plus,
  CheckCircle2,
  Loader2,
  Calendar,
  Trash2,
  ExternalLink,
  Upload,
  ChevronDown,
  FileUp,
  Pencil,
  Download,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import MobileQuickActions from '../components/layout/MobileQuickActions';
import { apiFetch } from '../fetchInterceptor';
import { downloadPdfBlob } from '../lib/downloadPdfBlob';
import RecentOCRUploads from '../components/RecentOCRUploads';
import { ARAPDashboardHeader } from '../components/arap';
import { SearchableContactSelect } from '../components/ui/searchable-contact-select';
import { SearchableAccountSelect } from '../components/ui/searchable-account-select';
import { SearchableDescriptionSelect } from '../components/ui/searchable-description-select';
import { CurrencyInput } from '../components/ui/currency-input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

const StatusBadge = ({ status }) => {
  const styles = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-200',
    approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    partial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    overdue: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    voided: 'bg-gray-100 text-gray-500 dark:bg-gray-900/30 dark:text-gray-500',
  };
  
  return (
    <Badge variant="secondary" className={cn('text-xs capitalize', styles[status])}>
      {status}
    </Badge>
  );
};


// Main Bills Page
export default function Bills() {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [approving, setApproving] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  // Row currently selected for mobile quick-actions bottom sheet
  const [mobileActionsBill, setMobileActionsBill] = useState(null);

  const handleDownloadBillPdf = async (billId, billNo) => {
    // Fetch the server-rendered branded bill PDF, then route through the
    // shared downloadPdfBlob helper (wrapper page + OS save dialog).
    setDownloadingPdf(billId);
    try {
      const resp = await apiFetch(`${API_URL}/api/bills/${billId}/pdf`);
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(e.detail || 'Could not fetch PDF');
      }
      const blob = await resp.blob();
      await downloadPdfBlob(blob, `Bill_${billNo || billId}.pdf`, { mode: 'download' });
    } catch (e) {
      toast.error(e?.message || 'Could not download bill');
    } finally {
      setDownloadingPdf(null);
    }
  };
  const [apMetrics, setApMetrics] = useState({});
  const [apAging, setApAging] = useState({});
  const [apInsight, setApInsight] = useState(null);
  const [upcomingBills, setUpcomingBills] = useState([]);
  
  // Legacy ?new=true → redirect to full-page form
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      navigate('/bills/new', { replace: true });
    }
  }, [searchParams, navigate]);
  
  const handleEditBill = (bill) => {
    navigate(`/bills/${bill.bill_id}/edit`);
  };
  
  const fetchBills = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setLoading(true);
    try {
      let url = `${API_URL}/api/bills?workspace_id=${currentWorkspace.workspace_id}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setBills(data);
        
        // Calculate AP metrics from bills data
        const today = new Date();
        const weekFromNow = new Date(today);
        weekFromNow.setDate(weekFromNow.getDate() + 7);
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        
        // Outstanding includes draft, approved, partial, and overdue (anything not paid)
        const outstanding = data.filter(b => ['draft', 'approved', 'partial', 'overdue'].includes(b.status));
        const overdue = data.filter(b => b.status === 'overdue');
        const dueThisWeek = outstanding.filter(b => {
          const dueDate = new Date(b.due_date);
          return dueDate >= today && dueDate <= weekFromNow;
        });
        const paidThisMonth = data.filter(b => 
          b.status === 'paid' && new Date(b.paid_date || b.updated_at) >= monthStart
        );
        
        setApMetrics({
          total: outstanding.reduce((sum, b) => sum + (b.balance_due || 0), 0),
          dueThisWeek: dueThisWeek.reduce((sum, b) => sum + (b.balance_due || 0), 0),
          dueThisWeekCount: dueThisWeek.length,
          overdue: overdue.reduce((sum, b) => sum + (b.balance_due || 0), 0),
          overdueCount: overdue.length,
          paidThisMonth: paidThisMonth.reduce((sum, b) => sum + (b.total || 0), 0)
        });
        
        // Calculate aging buckets based on days past due date
        // Include ALL unpaid bills (draft, approved, partial, overdue)
        const aging = { current: 0, days_1_30: 0, days_31_60: 0, days_61_90: 0, days_90_plus: 0 };
        const todayDate = new Date();
        todayDate.setHours(0, 0, 0, 0); // Normalize to start of day
        
        outstanding.forEach(bill => {
          const dueDate = new Date(bill.due_date + 'T00:00:00'); // Ensure proper parsing
          const timeDiff = todayDate.getTime() - dueDate.getTime();
          const daysOverdue = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
          const amount = bill.balance_due || 0;
          
          if (daysOverdue <= 0) aging.current += amount;
          else if (daysOverdue <= 30) aging.days_1_30 += amount;
          else if (daysOverdue <= 60) aging.days_31_60 += amount;
          else if (daysOverdue <= 90) aging.days_61_90 += amount;
          else aging.days_90_plus += amount;
        });
        setApAging(aging);
        
        // Get upcoming bills for timeline
        const upcoming = outstanding
          .filter(b => new Date(b.due_date) >= today)
          .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
          .slice(0, 5)
          .map(b => ({
            id: b.bill_id,
            name: b.vendor_name || `Bill #${b.bill_no}`,
            amount: b.balance_due,
            dueDate: b.due_date
          }));
        setUpcomingBills(upcoming);
        
        // Generate AI Buck insight
        if (dueThisWeek.length > 0) {
          const totalDue = dueThisWeek.reduce((sum, b) => sum + (b.balance_due || 0), 0);
          setApInsight({
            message: `${dueThisWeek.length} bills totaling $${totalDue.toLocaleString()} are due this week. Ensure sufficient cash for timely payments.`,
            action: { label: 'Review Due Bills', path: '/bills?due=week' }
          });
        } else if (overdue.length > 0) {
          setApInsight({
            message: `You have ${overdue.length} overdue bills. Paying these promptly can help maintain vendor relationships.`,
            action: { label: 'Pay Overdue', path: '/bills?status=overdue' }
          });
        } else {
          setApInsight(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch bills:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, statusFilter]);
  
  useEffect(() => {
    fetchBills();
  }, [fetchBills]);
  
  const handleApprove = async (billId) => {
    setApproving(billId);
    try {
      const response = await fetch(
        `${API_URL}/api/bills/${billId}/approve`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        toast.success('Bill approved');
        fetchBills();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve bill');
      }
    } catch (error) {
      toast.error('Failed to approve bill');
    } finally {
      setApproving(null);
    }
  };
  
  // Calculate summary (legacy - keep for backward compatibility)
  const totalOutstanding = bills.filter(b => ['approved', 'partial', 'overdue'].includes(b.status))
    .reduce((sum, b) => sum + b.balance_due, 0);
  const totalOverdue = bills.filter(b => b.status === 'overdue')
    .reduce((sum, b) => sum + b.balance_due, 0);
  
  return (
    <div className="space-y-6" data-testid="bills-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Bills</h1>
          <p className="text-muted-foreground">Manage accounts payable</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Upload Dropdown with Batch Import */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="upload-dropdown-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate('/invoice-ocr?type=bill')} data-testid="upload-single-bill-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload Single Bill
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/batch-import?type=bills')} data-testid="batch-import-bills-btn">
                <FileUp className="w-4 h-4 mr-2" />
                Batch Import (CSV)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => navigate('/bills/new')} data-testid="new-bill-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Bill
          </Button>
          <HelpPanel {...billsHelp} />
        </div>
      </div>
      
      {/* AP Dashboard Header */}
      <ARAPDashboardHeader
        type="ap"
        metrics={apMetrics}
        aging={apAging}
        insight={apInsight}
        upcomingItems={upcomingBills}
        loading={loading}
      />
      
      {/* Recent OCR Uploads */}
      <RecentOCRUploads documentType="bill" />
      
      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Bills Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 space-y-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : bills.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-muted-foreground">No bills yet</p>
              <p className="text-sm text-muted-foreground mt-1">Enter bills to track payables</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate('/bills/new')}>
                Create Your First Bill
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Bill #</TableHead>
                  <TableHead className="whitespace-nowrap">Vendor</TableHead>
                  <TableHead className="whitespace-nowrap">Date</TableHead>
                  <TableHead className="whitespace-nowrap">Due Date</TableHead>
                  <TableHead className="whitespace-nowrap">Status</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Total</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Balance</TableHead>
                  <TableHead className="text-right w-28 whitespace-nowrap">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bills.map(bill => (
                  <TableRow
                    key={bill.bill_id}
                    data-testid={`bill-${bill.bill_id}`}
                    className="cursor-pointer lg:cursor-default hover:bg-muted/30 lg:hover:bg-transparent transition-colors"
                    onClick={() => {
                      if (typeof window !== 'undefined'
                          && window.matchMedia
                          && window.matchMedia('(max-width: 1023px)').matches) {
                        setMobileActionsBill(bill);
                      }
                    }}
                  >
                    <TableCell className="whitespace-nowrap">#{bill.bill_no}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{bill.vendor_name}</p>
                        {bill.reference && <p className="text-xs text-muted-foreground">Ref: {bill.reference}</p>}
                      </div>
                    </TableCell>
                    <TableCell>{bill.bill_date}</TableCell>
                    <TableCell>{bill.due_date}</TableCell>
                    <TableCell><StatusBadge status={bill.status} /></TableCell>
                    <TableCell className="text-right">{formatCurrency(bill.total)}</TableCell>
                    <TableCell className="text-right">
                      <span className={bill.balance_due > 0 ? 'text-rose-600' : 'text-emerald-600'}>
                        {formatCurrency(bill.balance_due)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Quick download — server-rendered branded PDF */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadBillPdf(bill.bill_id, bill.bill_no)}
                          disabled={downloadingPdf === bill.bill_id}
                          data-testid={`download-bill-${bill.bill_id}`}
                          title="Download Bill PDF"
                        >
                          {downloadingPdf === bill.bill_id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Download className="w-4 h-4" />}
                        </Button>
                        {/* Edit button for draft and approved bills */}
                        {['draft', 'approved'].includes(bill.status) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditBill(bill)}
                            data-testid={`edit-bill-${bill.bill_id}`}
                            title="Edit Bill"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Approve button only for draft */}
                        {bill.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApprove(bill.bill_id)}
                            disabled={approving === bill.bill_id}
                            data-testid={`approve-bill-${bill.bill_id}`}
                            title="Approve Bill"
                          >
                            {approving === bill.bill_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                        {bill.journal_entry_id && (
                          <Button size="sm" variant="ghost" asChild title="View Journal Entry">
                            <Link to={`/journal-entries/${bill.journal_entry_id}`}>
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile quick-actions bottom sheet — 2-tap actions */}
      {mobileActionsBill && (
        <MobileQuickActions
          open={!!mobileActionsBill}
          onClose={() => setMobileActionsBill(null)}
          title={`#${mobileActionsBill.bill_no} · ${mobileActionsBill.vendor_name}`}
          subtitle={`${(mobileActionsBill.status || '').toUpperCase()} · ${formatCurrency(mobileActionsBill.total)}`}
          actions={[
            {
              icon: Eye,
              label: 'View details',
              variant: 'primary',
              onClick: () => navigate(`/bills/${mobileActionsBill.bill_id}/edit`),
              testid: `quick-view-bill-${mobileActionsBill.bill_id}`,
            },
            ...(mobileActionsBill.status === 'draft' ? [{
              icon: CheckCircle2,
              label: 'Approve',
              variant: 'primary',
              disabled: approving === mobileActionsBill.bill_id,
              onClick: () => handleApprove(mobileActionsBill.bill_id),
              testid: `quick-approve-bill-${mobileActionsBill.bill_id}`,
            }] : []),
            {
              icon: Download,
              label: 'Download PDF',
              disabled: downloadingPdf === mobileActionsBill.bill_id,
              onClick: () => handleDownloadBillPdf(mobileActionsBill.bill_id, mobileActionsBill.bill_no),
              testid: `quick-download-bill-${mobileActionsBill.bill_id}`,
            },
            ...(['draft', 'approved'].includes(mobileActionsBill.status) ? [{
              icon: Pencil,
              label: 'Edit',
              onClick: () => handleEditBill(mobileActionsBill),
              testid: `quick-edit-bill-${mobileActionsBill.bill_id}`,
            }] : []),
            ...(mobileActionsBill.journal_entry_id ? [{
              icon: ExternalLink,
              label: 'View journal entry',
              onClick: () => navigate(`/journal-entries/${mobileActionsBill.journal_entry_id}`),
              testid: `quick-je-bill-${mobileActionsBill.bill_id}`,
            }] : []),
          ]}
        />
      )}

    </div>
  );
}
